// SMO Lead Raporları › ekip izinleri: iki koleksiyondaki kayıtları tek biçime
// indirger ve bir yıl için özet (bakiye, sıklık, aylara/türlere dağılım) çıkarır.
// Bakiye İzin Yönetimi ile aynı hesaptır (utils/leaveBalance.computeAnnualBalance).

import { computeAnnualBalance } from '../../utils/leaveBalance';
import { parseLeaveDate } from '../../utils/employeeLeaves';

const DAY_MS = 24 * 60 * 60 * 1000;

const toDate = (value) => {
  if (!value) return null;
  try {
    const date = value.toDate ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

// "09:00" biçimindeki iki saat arasındaki süre (saat)
const hoursBetween = (from, to) => {
  const parse = (text) => {
    const match = String(text || '').match(/^(\d{1,2}):(\d{2})/);
    return match ? Number(match[1]) * 60 + Number(match[2]) : null;
  };
  const start = parse(from);
  const end = parse(to);
  if (start === null || end === null || end <= start) return 0;
  return Math.round(((end - start) / 60) * 100) / 100;
};

/**
 * LeaveRequests (çalışanın talebi) ve EmployeeLeaves (İzin Yönetimi › İzin Ekle)
 * kayıtlarını ortak alanlara çevirir. `leaveType` + `startDate` bakiye
 * hesabının beklediği adlarla tutulur. Saat alanları bugün formlarda yok;
 * bir kayıtta bulunursa ekranda gösterilir.
 */
export const toLeaveRecord = (raw, source) => {
  const start = parseLeaveDate(raw.startDate);
  // EmployeeLeaves'te son izin günü workReturnDate alanındadır
  const end = parseLeaveDate(source === 'EmployeeLeaves' ? (raw.workReturnDate || raw.endDate) : raw.endDate) || start;
  const startTime = raw.startTime || raw.leaveStartTime || '';
  const endTime = raw.endTime || raw.leaveEndTime || '';
  return {
    id: raw.id,
    source,
    employeeId: raw.employeeId,
    leaveType: raw.leaveType || raw.typeKey || null,
    startDate: raw.startDate,
    status: raw.status || 'APPROVED', // eski EmployeeLeaves kayıtlarında status yok → onaylı
    start,
    end: end && start && end < start ? start : end,
    leaveDays: Number(raw.leaveDays) || 0,
    travelDays: Number(raw.travelDays) || 0,
    startTime,
    endTime,
    leaveHours: Number(raw.leaveHours || raw.hours) || hoursBetween(startTime, endTime),
    reason: raw.reason || '',
    address: raw.address || '',
    reportFile: raw.reportFile?.url ? raw.reportFile : null,
    createdAt: toDate(raw.createdAt),
    processedAt: toDate(raw.processedAt),
    processedBy: raw.processedBy || '',
    adminNote: raw.adminNote || '',
  };
};

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const byStart = (a, b) => (a.start?.getTime() || 0) - (b.start?.getTime() || 0);

/** Bugün izinde mi, son ve sıradaki izin — yıldan bağımsız. */
export const leaveTimeline = (records = [], today = startOfToday()) => {
  const dated = records.filter((r) => r.start && r.status !== 'REJECTED').sort(byStart);
  const approved = dated.filter((r) => r.status === 'APPROVED');
  return {
    current: approved.find((r) => r.start <= today && r.end >= today) || null,
    last: [...approved].reverse().find((r) => r.end < today) || null,
    next: dated.find((r) => r.start > today) || null,
  };
};

/** Tarih ile bugün arasındaki tam gün farkı (geçmiş negatif). */
export const daysFromToday = (date, today = startOfToday()) => Math.round((date.getTime() - today.getTime()) / DAY_MS);

/**
 * Bir yıl için özet. Sayımlar onaylı izinlerden yapılır; bekleyen ve
 * reddedilenler ayrıca sayılır. Yıl, iznin başladığı yıldır (bakiyeyle aynı).
 */
export const summarizeLeaves = ({ records = [], entitlement, carryFromYear, year, today = startOfToday() }) => {
  const balance = computeAnnualBalance({ entitlement, leaveRequests: records, employeeLeaves: [], year, carryFromYear });
  const inYear = records.filter((r) => r.start && r.start.getFullYear() === year);
  const approved = inYear.filter((r) => r.status === 'APPROVED').sort(byStart);

  const byMonth = Array.from({ length: 12 }, () => ({ count: 0, days: 0, hours: 0 }));
  const typeMap = new Map();
  approved.forEach((r) => {
    const month = byMonth[r.start.getMonth()];
    month.count += 1;
    month.days += r.leaveDays;
    month.hours += r.leaveHours;
    const type = typeMap.get(r.leaveType) || { typeKey: r.leaveType, count: 0, days: 0, hours: 0 };
    type.count += 1;
    type.days += r.leaveDays;
    type.hours += r.leaveHours;
    typeMap.set(r.leaveType, type);
  });

  // Sıklık: ardışık izin başlangıçları arasındaki ortalama gün ve aylık ortalama
  const gaps = approved.slice(1).map((r, i) => (r.start - approved[i].start) / DAY_MS);
  const averageGapDays = gaps.length > 0 ? Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length) : null;
  const monthsElapsed = year === today.getFullYear() ? today.getMonth() + 1 : 12;

  return {
    balance,
    approvedCount: approved.length,
    pendingCount: inYear.filter((r) => r.status === 'PENDING').length,
    rejectedCount: inYear.filter((r) => r.status === 'REJECTED').length,
    totalDays: approved.reduce((acc, r) => acc + r.leaveDays, 0),
    totalHours: approved.reduce((acc, r) => acc + r.leaveHours, 0),
    perMonth: year > today.getFullYear() ? 0 : Math.round((approved.length / monthsElapsed) * 10) / 10,
    averageGapDays,
    byMonth,
    byType: [...typeMap.values()].sort((a, b) => b.days - a.days || b.count - a.count),
    records: [...inYear].sort((a, b) => byStart(b, a)),
  };
};

/** Seçilebilecek yıllar: bu yıl + kaydı olan yıllar, yeniden eskiye. */
export const leaveYears = (records = [], currentYear = new Date().getFullYear()) => (
  [...new Set([currentYear, ...records.map((r) => r.start?.getFullYear()).filter(Boolean)])]
    .sort((a, b) => b - a)
);
